import { createRoute, Link } from '@tanstack/react-router';
import { Globe, Heart } from 'lucide-react';
import { useEffect } from 'react';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { KOFI_URL } from '../constants/external';
import { Route as rootRoute } from './__root';
import styles from './refund.module.css';
import landingStyles from './index.module.css';

export const Route = createRoute({
    getParentRoute: () => rootRoute,
    path: '/refund',
    component: RefundPage,
});

function RefundPage() {
    const { preferences, setPreference } = usePreferences();
    const { t, locale } = useTranslation();
    // Force Vivid theme on public pages, restore user's theme on leave
    useEffect(() => {
        setThemeOverride(true);
        applyTheme('vivid');
        return () => {
            setThemeOverride(false);
            applyTheme(preferences.theme);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleLocale = (next: Locale) => setPreference('locale', next);

    const sections = [
        { title: t('refund.overviewTitle'), body: t('refund.overviewBody') },
        { title: t('refund.eligibilityTitle'), body: t('refund.eligibilityBody') },
        { title: t('refund.howToTitle'), body: t('refund.howToBody') },
        { title: t('refund.processingTitle'), body: t('refund.processingBody') },
        { title: t('refund.exceptionsTitle'), body: t('refund.exceptionsBody') },
        { title: t('refund.contactTitle'), body: t('refund.contactBody') },
    ];

    return (
        <div className={styles.page}>
            {/* Controls */}
            <div className={landingStyles.controls}>
                <div className={landingStyles.langPill} role="group" aria-label="Language">
                    <Globe size={13} strokeWidth={1.75} className={landingStyles.langIcon} />
                    {(['en', 'he'] as Locale[]).map((loc) => (
                        <button
                            key={loc}
                            className={`${landingStyles.langOption} ${locale === loc ? landingStyles.langOptionActive : ''}`}
                            onClick={() => toggleLocale(loc)}
                            aria-pressed={locale === loc}
                        >
                            {loc.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Header */}
            <div className={styles.header}>
                <Link to="/" className={styles.backLink}>
                    <span className={styles.backPrompt}>&gt; </span>
                    SiliconVault
                </Link>
                <h1 className={styles.title}>{t('refund.title')}</h1>
                <p className={styles.lastUpdated}>{t('refund.lastUpdated')}</p>
            </div>

            {/* Content */}
            <div className={styles.content}>
                <div className={styles.card}>
                    {sections.map((s) => (
                        <div key={s.title} className={styles.section}>
                            <h2 className={styles.sectionTitle}>{s.title}</h2>
                            <p className={styles.sectionBody}>{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <span className={styles.footerText}>
                    &copy; {new Date().getFullYear()} Asaf Saar. {t('landing.footer')}
                </span>
                <span className={styles.footerSep}> | </span>
                <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
                <span className={styles.footerSep}> | </span>
                <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
                <span className={styles.footerSep}> | </span>
                <a
                    href={KOFI_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.footerSupport}
                >
                    <Heart size={10} strokeWidth={2} />
                    {t('support.footerLink')}
                </a>
            </footer>
        </div>
    );
}
